'use client';

import React, { useState } from 'react';
import {
    ShieldCheck,
    Plus,
    Users,
    ChevronDown,
    Settings2,
    Lock,
    Eye,
    Trash2,
    X,
    Check,
    Fingerprint,
    ShieldAlert,
    LayoutDashboard,
    Banknote,
    FileText,
    Database,
    CheckCircle2
} from 'lucide-react';
import Link from 'next/link';

// --- Types ---

interface Permission {
    module: string;
    action: 'lihat' | 'tambah/edit' | 'hapus' | 'approve';
    key: string; // e.g., 'dashboard_read', 'finance_write'
}

interface Role {
    id: string;
    name: string;
    description: string;
    userCount: number;
    accessLevel: 'Owner' | 'Admin' | 'Operator' | 'Guest';
    status: 'Aktif' | 'Nonaktif';
    isSystem: boolean; // Cannot be deleted
    permissions: string[]; // List of permission keys
}

// --- Mock Data ---

const PERMISSION_STRUCTURE = [
    {
        category: 'Dashboard',
        icon: LayoutDashboard,
        items: [
            { label: 'Lihat Ringkasan', key: 'dashboard_view' }
        ]
    },
    {
        category: 'Keuangan',
        icon: Banknote,
        items: [
            { label: 'Input Setoran', key: 'finance_deposit' },
            { label: 'Input Pengeluaran', key: 'finance_expense' },
            { label: 'Approval Transaksi', key: 'finance_approve' }
        ]
    },
    {
        category: 'Laporan',
        icon: FileText,
        items: [
            { label: 'Lihat Jurnal', key: 'report_journal' },
            { label: 'Lihat Buku Besar', key: 'report_ledger' },
            { label: 'Lihat Neraca/Laba Rugi', key: 'report_fs' }
        ]
    },
    {
        category: 'Master Data',
        icon: Database,
        items: [
            { label: 'Kelola Akun Jurnal', key: 'master_coa' },
            { label: 'Kelola Manajemen Akun', key: 'master_users' },
            { label: 'Kelola Role & Permissions', key: 'master_roles' }
        ]
    }
];

const INITIAL_ROLES: Role[] = [
    {
        id: '1',
        name: 'Admin Sistem',
        description: 'Akses penuh ke seluruh fitur dan pengaturan sistem.',
        userCount: 2,
        accessLevel: 'Owner',
        status: 'Aktif',
        isSystem: true,
        permissions: ['dashboard_view', 'finance_deposit', 'finance_expense', 'finance_approve', 'report_journal', 'report_ledger', 'report_fs', 'master_coa', 'master_users', 'master_roles']
    },
    {
        id: '2',
        name: 'Kepala Sekolah',
        description: 'Otoritas tertinggi unit untuk approval dan pemantauan laporan.',
        userCount: 1,
        accessLevel: 'Admin',
        status: 'Aktif',
        isSystem: true,
        permissions: ['dashboard_view', 'finance_approve', 'report_journal', 'report_ledger', 'report_fs']
    },
    {
        id: '3',
        name: 'Bendahara',
        description: 'Tanggung jawab penuh atas input dan mutasi keuangan harian.',
        userCount: 3,
        accessLevel: 'Operator',
        status: 'Aktif',
        isSystem: false,
        permissions: ['dashboard_view', 'finance_deposit', 'finance_expense', 'report_journal', 'report_ledger']
    },
    {
        id: '4',
        name: 'Viewer (Read-only)',
        description: 'Hanya dapat melihat laporan tanpa hak akses modifikasi data.',
        userCount: 5,
        accessLevel: 'Guest',
        status: 'Aktif',
        isSystem: false,
        permissions: ['dashboard_view', 'report_journal', 'report_ledger', 'report_fs']
    }
];

export default function RolePermissionsPage() {
    // --- State ---
    const [roles, setRoles] = useState<Role[]>(INITIAL_ROLES);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRole, setSelectedRole] = useState<Role | null>(null);

    // --- Action Handlers ---
    const handleEditRole = (role: Role) => {
        setSelectedRole(role);
        setIsModalOpen(true);
    };

    const handleAddRole = () => {
        setSelectedRole(null);
        setIsModalOpen(true);
    };

    const handleDelete = (id: string) => {
        if (confirm('Apakah Anda yakin ingin menghapus role ini?')) {
            setRoles(prev => prev.filter(r => r.id !== id));
        }
    };

    return (
        <main className="p-4 md:p-10 space-y-8 max-w-7xl mx-auto w-full pb-32 animate-in fade-in duration-700">
            {/* Header */}
            <section className="bg-surface p-8 rounded-4xl border border-border-main shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-primary-soft/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
                <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div className="space-y-4">
                        <div className="flex items-center gap-2 text-[10px] font-bold text-muted uppercase tracking-widest">
                            <Link href="/sekolah" className="hover:text-primary transition-all">Manajemen Keamanan</Link>
                            <span className="text-muted/50">•</span>
                            <span className="text-foreground">Role & Permissions</span>
                        </div>
                        <div className="flex items-center gap-6">
                            <div className="w-16 h-16 rounded-2xl bg-primary-soft/50 border border-primary/10 flex items-center justify-center text-primary shadow-inner">
                                <ShieldCheck className="w-8 h-8" />
                            </div>
                            <div className="space-y-1">
                                <h1 className="text-3xl md:text-4xl font-black text-foreground tracking-tight whitespace-nowrap">Role & Permissions</h1>
                                <p className="text-secondary font-medium flex items-center gap-2 text-sm uppercase tracking-wider">
                                    <Lock className="w-3.5 h-3.5 text-primary" /> Pengaturan Hak Akses Pengguna Sekolah
                                </p>
                            </div>
                        </div>
                    </div>

                    <button
                        onClick={handleAddRole}
                        className="flex items-center gap-3 px-6 py-3.5 bg-primary text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl shadow-primary/20 hover:scale-[1.02] transition-all"
                    >
                        <Plus className="w-4 h-4" /> Tambah Role Baru
                    </button>
                </div>
            </section>

            {/* Table */}
            <div className="bg-surface border border-border-main rounded-3xl overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-background/50 border-b border-border-main">
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Nama Role</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest text-center">User</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest w-[160px] text-center">Akses Level</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest w-[120px] text-center">Status</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest w-[180px] text-center">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border-main/50">
                            {roles.map((role) => (
                                <tr key={role.id} className="hover:bg-primary-soft/5 transition-colors group">
                                    <td className="px-6 py-6 font-medium">
                                        <div className="space-y-1">
                                            <div className="flex items-center gap-3">
                                                <p className="text-sm font-black text-foreground capitalize">{role.name}</p>
                                                {role.isSystem && <Lock className="w-3 h-3 text-muted" />}
                                            </div>
                                            <p className="text-[10px] text-secondary whitespace-nowrap overflow-hidden text-ellipsis max-w-[300px] font-medium forced-line-clamp-1">{role.description}</p>
                                        </div>
                                    </td>
                                    <td className="px-6 py-6 text-center">
                                        <div className="flex items-center justify-center gap-2">
                                            <div className="w-8 h-8 rounded-full bg-background border border-border-main flex items-center justify-center text-[10px] font-black text-primary">
                                                <Users className="w-4 h-4" />
                                            </div>
                                            <span className="text-xs font-black text-foreground">{role.userCount}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-6 text-center">
                                        <span className={`px-2.5 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest border ${role.accessLevel === 'Owner' ? 'bg-error-soft text-error border-error/20' :
                                            role.accessLevel === 'Admin' ? 'bg-primary-soft text-primary border-primary/20' :
                                                role.accessLevel === 'Operator' ? 'bg-success-soft text-success border-success/20' :
                                                    'bg-muted/10 text-muted border-muted/20'
                                            }`}>
                                            {role.accessLevel}
                                        </span>
                                    </td>
                                    <td className="px-6 py-6 text-center">
                                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-wider border ${role.status === 'Aktif' ? 'bg-success-soft text-success border-success/10' : 'bg-muted/10 text-muted border-muted/10'
                                            }`}>
                                            <div className={`w-1.5 h-1.5 rounded-full ${role.status === 'Aktif' ? 'bg-success' : 'bg-muted'}`}></div>
                                            {role.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-6 text-center">
                                        <div className="flex items-center justify-center gap-2">
                                            <button
                                                onClick={() => handleEditRole(role)}
                                                className="flex items-center gap-2 px-3 py-2 bg-background border border-border-main rounded-xl text-[9px] font-bold uppercase tracking-widest text-muted hover:text-primary hover:border-primary/30 transition-all shadow-sm group/btn"
                                            >
                                                <Settings2 className="w-3.5 h-3.5 group-hover/btn:rotate-45 transition-transform" /> {role.isSystem ? 'View' : 'Edit'}
                                            </button>
                                            {!role.isSystem && (
                                                <button
                                                    onClick={() => handleDelete(role.id)}
                                                    className="p-2.5 rounded-xl text-muted hover:text-error hover:bg-error-soft transition-all"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Form & Permission Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-surface rounded-4xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col animate-in zoom-in-95 duration-300">
                        <div className="p-8 border-b border-border-main bg-primary-soft/5 flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-white rounded-2xl border border-primary/10 flex items-center justify-center text-primary shadow-sm">
                                    <Settings2 className="w-6 h-6" />
                                </div>
                                <div>
                                    <h3 className="text-xl font-black text-foreground">{selectedRole ? (selectedRole.isSystem ? 'Detail Permission' : 'Edit Role') : 'Tambah Role Baru'}</h3>
                                    <p className="text-[10px] font-black text-primary uppercase tracking-widest">Keamanan & Hak Akses</p>
                                </div>
                            </div>
                            <button onClick={() => setIsModalOpen(false)} className="p-2 rounded-full hover:bg-secondary-soft/50 text-muted transition-all"><X className="w-5 h-5" /></button>
                        </div>

                        <div className="p-8 overflow-y-auto space-y-10 flex-1 custom-scrollbar">
                            {/* Role Basic Info */}
                            <div className="grid grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-muted uppercase tracking-widest ml-1">Nama Role</label>
                                    <input
                                        type="text"
                                        readOnly={selectedRole?.isSystem}
                                        defaultValue={selectedRole?.name}
                                        placeholder="Contoh: Staff Keuangan"
                                        className="w-full bg-background border border-border-main rounded-2xl py-3.5 px-6 text-sm font-bold outline-none focus:ring-4 focus:ring-primary/5 focus:border-primary/20 transition-all"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-muted uppercase tracking-widest ml-1">Level Akses</label>
                                    <div className="relative">
                                        <select
                                            disabled={selectedRole?.isSystem}
                                            defaultValue={selectedRole?.accessLevel}
                                            className="w-full bg-background border border-border-main rounded-2xl py-3.5 px-6 text-sm font-bold outline-none appearance-none cursor-pointer focus:ring-4 focus:ring-primary/5 focus:border-primary/20 transition-all disabled:opacity-50"
                                        >
                                            <option>Owner</option>
                                            <option>Admin</option>
                                            <option>Operator</option>
                                            <option>Guest</option>
                                        </select>
                                        <ChevronDown className="absolute right-6 top-1/2 -translate-y-1/2 w-4 h-4 text-muted pointer-events-none" />
                                    </div>
                                </div>
                                <div className="col-span-2 space-y-2">
                                    <label className="text-[10px] font-black text-muted uppercase tracking-widest ml-1">Deskripsi & Peran</label>
                                    <textarea
                                        rows={2}
                                        readOnly={selectedRole?.isSystem}
                                        defaultValue={selectedRole?.description}
                                        className="w-full bg-background border border-border-main rounded-2xl p-6 text-sm font-medium outline-none focus:ring-4 focus:ring-primary/5 focus:border-primary/20 transition-all resize-none disabled:opacity-50"
                                        placeholder="Jelaskan tanggung jawab utama role ini..."
                                    ></textarea>
                                </div>
                            </div>

                            {/* Permission Matrix */}
                            <div className="space-y-6">
                                <div className="flex items-center justify-between border-b border-border-main pb-4">
                                    <h4 className="text-sm font-black text-foreground uppercase tracking-widest">Matriks Permission (Checklist)</h4>
                                    <div className="flex items-center gap-2 text-primary font-bold text-[10px] uppercase tracking-tighter bg-primary-soft/30 px-3 py-1.5 rounded-full">
                                        <Fingerprint className="w-3.5 h-3.5" /> Kontrol Akses Berbasis Role (RBAC)
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    {PERMISSION_STRUCTURE.map((section, sIdx) => (
                                        <div key={sIdx} className="space-y-4">
                                            <div className="flex items-center gap-3">
                                                <div className="p-2 bg-background border border-border-main rounded-xl text-muted group-hover:text-primary transition-all">
                                                    <section.icon className="w-4 h-4" />
                                                </div>
                                                <h5 className="text-[11px] font-black text-muted uppercase tracking-widest">{section.category}</h5>
                                            </div>
                                            <div className="space-y-3 bg-background/50 rounded-3xl p-6 border border-border-main">
                                                {section.items.map((item, iIdx) => {
                                                    const isChecked = selectedRole?.permissions.includes(item.key);
                                                    return (
                                                        <label key={iIdx} className={`flex items-center justify-between group flex-row-reverse cursor-pointer ${selectedRole?.isSystem ? 'pointer-events-none' : ''}`}>
                                                            <div className="relative w-10 h-6">
                                                                <input
                                                                    type="checkbox"
                                                                    className="peer sr-only"
                                                                    defaultChecked={isChecked}
                                                                />
                                                                <div className="w-full h-full bg-muted/20 rounded-full peer peer-checked:bg-primary transition-all after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-4"></div>
                                                            </div>
                                                            <div className="flex items-center gap-3">
                                                                {isChecked ? <div className="w-4 h-4 bg-success text-white rounded-full flex items-center justify-center p-1 shadow-sm"><Check className="w-2.5 h-2.5" /></div> : <div className="w-4 h-4 bg-muted/20 rounded-full"></div>}
                                                                <span className="text-xs font-bold text-secondary group-hover:text-foreground transition-all">{item.label}</span>
                                                            </div>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="p-8 bg-background/50 border-t border-border-main flex gap-4">
                            <button onClick={() => setIsModalOpen(false)} className="px-8 py-4 bg-white border border-border-main rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-secondary-soft/50 transition-all flex-1">
                                {selectedRole?.isSystem ? 'Tutup' : 'Batal'}
                            </button>
                            {!selectedRole?.isSystem && (
                                <button onClick={() => setIsModalOpen(false)} className="px-8 py-4 bg-primary text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl shadow-primary/20 hover:scale-[1.02] transition-all flex-1">
                                    Simpan Konfigurasi
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </main>
    );
}
